import { useState } from 'react';
import { Box, Collapse, Stack, Typography, alpha, useTheme } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import type { HansAckerFinding, HansAckerReport, HansAckerSeverity } from './types';
import { InlineText } from './Rich';
import { SEVERITIES, donutGradient, severityCounts } from './utils';

interface Props {
  report: HansAckerReport;
  scopedFindings: HansAckerFinding[];
  onSelectSeverity: (s: HansAckerSeverity) => void;
}

export default function PostureSummary({ report, scopedFindings, onSelectSeverity }: Props) {
  const theme = useTheme();
  const [aboutOpen, setAboutOpen] = useState(false);
  const counts = severityCounts(scopedFindings);
  const total = scopedFindings.length;
  // Overall posture accent follows the highest severity in the report (slate when nothing was filed).
  const reportCounts = severityCounts(report.findings);
  const accent = SEVERITIES.find((s) => reportCounts[s.id] > 0)?.color ?? '#64748b';

  return (
    <Box
      id="hans-acker-summary"
      sx={{
        scrollMarginTop: 72,
        borderRadius: 3,
        border: '1px solid',
        borderColor: alpha(accent, 0.35),
        borderTop: `3px solid ${accent}`,
        background: `linear-gradient(180deg, ${alpha(accent, theme.palette.mode === 'dark' ? 0.12 : 0.06)} 0%, ${theme.palette.background.paper} 60%)`,
        p: 3,
      }}
    >
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems={{ md: 'center' }}>
        <Box sx={{ flex: '0 1 400px', minWidth: 300, border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2, bgcolor: alpha(theme.palette.text.primary, 0.03) }}>
          <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color: 'text.secondary', mb: 1.75 }}>
            Findings by severity
          </Typography>
          <Stack direction="row" spacing={2.5} alignItems="center">
            <Box sx={{ position: 'relative', width: 116, height: 116, borderRadius: '50%', flexShrink: 0, background: donutGradient(counts), display: 'grid', placeItems: 'center' }}>
              <Box sx={{ position: 'absolute', inset: 14, borderRadius: '50%', bgcolor: 'background.paper' }} />
              <Box sx={{ position: 'relative', textAlign: 'center', lineHeight: 1 }}>
                <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>{total}</Typography>
                <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.7, textTransform: 'uppercase', color: 'text.secondary', mt: 0.5 }}>Findings</Typography>
              </Box>
            </Box>
            <Stack spacing={1} sx={{ flex: 1 }}>
              {SEVERITIES.map((s) => (
                <Stack key={s.id} direction="row" alignItems="center" spacing={1.25}>
                  <Box sx={{ width: 10, height: 10, borderRadius: 0.75, bgcolor: s.color, flexShrink: 0 }} />
                  <Typography sx={{ flex: 1, fontSize: 13, color: 'text.secondary' }}>{s.label}</Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{counts[s.id]}</Typography>
                </Stack>
              ))}
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Box
              sx={{
                width: 8, height: 8, borderRadius: '50%', bgcolor: accent,
                animation: 'haPulse 2s infinite',
                '@keyframes haPulse': { '70%': { boxShadow: `0 0 0 9px ${alpha(accent, 0)}` }, '100%': { boxShadow: `0 0 0 0 ${alpha(accent, 0)}` }, '0%': { boxShadow: `0 0 0 0 ${alpha(accent, 0.6)}` } },
              }}
            />
            <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.9, textTransform: 'uppercase', color: accent }}>{report.verdict.label}</Typography>
          </Stack>
          <Typography sx={{ fontSize: 30, fontWeight: 800, lineHeight: 1.15, letterSpacing: -0.3, mt: 0.75, mb: 1.5 }}>{report.verdict.headline}</Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderLeftColor: accent, borderLeftWidth: 3, borderRadius: 1.5, px: 2, py: 1.25, bgcolor: alpha(theme.palette.text.primary, 0.03), maxWidth: 640 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color: accent, mb: 0.5 }}>Business risk</Typography>
            <Typography sx={{ fontSize: 13, lineHeight: 1.6, color: 'text.primary' }}>{report.businessRisk}</Typography>
          </Box>
        </Box>
      </Stack>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))', gap: 1.5, mt: 3 }}>
        {SEVERITIES.map((s) => {
          const disabled = counts[s.id] === 0;
          return (
            <Box
              key={s.id}
              role="button"
              tabIndex={disabled ? -1 : 0}
              onClick={() => !disabled && onSelectSeverity(s.id)}
              onKeyDown={(e) => { if (!disabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onSelectSeverity(s.id); } }}
              sx={{
                display: 'flex', flexDirection: 'column', gap: 1, p: 2, borderRadius: 2,
                border: '1px solid', borderColor: 'divider', borderTop: `3px solid ${s.color}`,
                bgcolor: alpha(theme.palette.text.primary, 0.02),
                cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.45 : 1,
                transition: 'transform .15s, background-color .15s, border-color .15s',
                '&:hover': disabled ? {} : { transform: 'translateY(-3px)', bgcolor: alpha(s.color, 0.08), borderColor: alpha(s.color, 0.5) },
                '&:hover .ha-go': { color: s.color },
              }}
            >
              <Box sx={{ alignSelf: 'flex-start', fontSize: 10.5, fontWeight: 800, letterSpacing: 0.7, textTransform: 'uppercase', color: s.color, bgcolor: alpha(s.color, 0.14), borderRadius: 0.75, px: 1, py: 0.4 }}>
                {s.label}
              </Box>
              <Typography sx={{ flex: 1, fontSize: 13, lineHeight: 1.55, color: 'text.primary' }}>
                <InlineText text={report.severitySummary[s.id]} />
              </Typography>
              <Typography className="ha-go" sx={{ fontSize: 11, fontWeight: 700, color: 'text.disabled', transition: 'color .15s' }}>
                {disabled ? 'No findings in this scope' : 'View findings \u2192'}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ mt: 2 }}>
        <Box
          component="button"
          type="button"
          onClick={() => setAboutOpen((o) => !o)}
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75, border: 0, background: 'none', p: 0, cursor: 'pointer', color: 'text.secondary', fontSize: 12.5, fontWeight: 600, fontFamily: 'inherit' }}
        >
          <Box sx={{ display: 'flex', transform: aboutOpen ? 'rotate(180deg)' : 'none', transition: 'transform .2s' }}><ChevronDown size={15} /></Box>
          About this scan (methodology and scope)
        </Box>
        <Collapse in={aboutOpen}>
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.65, color: 'text.secondary', mt: 1 }}>{report.scanDetails}</Typography>
        </Collapse>
      </Box>
    </Box>
  );
}
